<script lang="ts">
import { RouteNames } from "../../../../utils/constants.ts";

export function getRouteSignupVerifyEmail() {
  return {
    name: RouteNames.SIGNUP_EMAIL_VERIFY
  };
}
</script>
<script setup lang="ts">
import VueCountdown from "@chenfengyuan/vue-countdown";

import { computed, onMounted, Ref, ref, watch } from "vue";
import { useRouter } from "vue-router";

import LinkButton from "../../../../components/buttons/LinkButton.vue";
import OtpInput from "../../../../components/form/OtpInput.vue";
import Signup from "../../../../components/layout/GuestLayout/Signup.vue";
import { IChallengeResponse } from "@/repositories/dto/user/IChallengeResponse.ts";
import { IRepositoryError } from "@/repositories/dto/errors/IRepositoryError.ts";
import { ISolutionResponse } from "@/repositories/dto/user/ISolutionResponse.ts";
import Spinner from "../../../../components/icons/Spinner.vue";
import { error, success } from "@/utils/toast.ts";
import { IMfaChallengePurpose } from "@/repositories/implementations/UserRepository.ts";
import usePersonStore from "@/store/personStore.ts";
import { getRouteSignupPhone } from "./Phone.vue";

const router = useRouter();
const personStore = usePersonStore();
const { person } = personStore;

const challengeCode: Ref<string | null> = ref(null);
const solutionCode: Ref<string> = ref("");
const inLoading: Ref<boolean> = ref(false);
const codeResendTime: Ref<number> = ref(0);
const counting: Ref<boolean> = ref(true);
const solutionLength = 6;
const isDevMode = computed(() => import.meta.env.DEV);

watch(solutionCode, async () => {
  if (solutionCode.value.length !== solutionLength) {
    return;
  }

  await verifySolution(challengeCode.value!, solutionCode.value);
});

onMounted(async () => {
  await getChallenge();
});

async function getChallenge(previousChallenge: string | null = null) {
  inLoading.value = true;
  let challenge = await personStore.getChallenge(IMfaChallengePurpose.ValidateEmail, previousChallenge);
  inLoading.value = false;

  if (challenge.hasOwnProperty("timeout")) {
    challenge = challenge as IChallengeResponse;
    challengeCode.value = challenge.code;
    codeResendTime.value = challenge.timeout;
    success("Email sent");
  } else {
    challenge = challenge as IRepositoryError;
    error(challenge.description);
  }
}

async function verifySolution(challenge: string, _solutionCode: string) {
  // Test mode: allow code 000000 in development
  if (_solutionCode === "000000" && import.meta.env.DEV) {
    success("Email verified (test mode)");
    setTimeout(async () => {
      await router.push(getRouteSignupPhone());
    }, 1000);
    return;
  }

  inLoading.value = true;
  let solution = await personStore.verifySolution(challenge, _solutionCode);
  inLoading.value = false;

  if (solution.hasOwnProperty("verified")) {
    solution = solution as ISolutionResponse;
    if (solution.verified) {
      success("Email verified");
      // Redirect to phone verification after successful email verification
      setTimeout(async () => {
        await router.push(getRouteSignupPhone());
      }, 1000);
    } else {
      challengeCode.value = solution.challenge.code;
      codeResendTime.value = solution.challenge.timeout;
      solutionCode.value = "";
      error("Error verifying email");
    }
  } else {
    solution = solution as IRepositoryError;
    error(solution.description);
  }
}

const resendCode = async () => {
  await getChallenge(challengeCode.value!);
};
</script>

<template>
  <Signup class="relative">
    <div class="mx-auto mt-4 md:mt-[40px] w-full max-w-[636px] px-4 md:px-0 flex-col justify-center items-center gap-6 md:gap-8 flex">
      <div class="flex-col justify-start items-center gap-6 md:gap-8 flex">
        <div class="text-center text-zinc-800 text-2xl md:text-4xl font-bold font-['Sora'] leading-tight md:leading-[38px]">
          Verify Your Email
        </div>
        <div class="text-center text-zinc-800 text-sm md:text-base leading-6 md:leading-[38px] font-normal font-['Poppins']">
          We've just emailed you to
          <span class="text-blue-500">
            {{ person!.pendingEmail }}
          </span>
          with a verification code
        </div>
        <div class="justify-center items-start gap-4 md:gap-8 inline-flex w-full">
          <OtpInput :length="solutionLength" v-model="solutionCode" />
        </div>
        <!-- Test mode hint -->
        <div v-if="isDevMode" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs md:text-sm w-full">
          <span class="text-yellow-800 font-semibold">🧪 Test Mode:</span>
          <span class="text-yellow-700"> Use code <code class="bg-yellow-100 px-2 py-1 rounded font-mono">000000</code> to skip verification</span>
        </div>
        <div class="text-center text-sm md:text-base">
          <span class="text-zinc-800 font-normal font-['Poppins'] leading-6 md:leading-[38px]">
            Didn't receive a code?
            <span v-if="counting">You can send a code again in </span>
            <LinkButton v-else class="inline" @click="resendCode">Resend</LinkButton>
          </span>
          <span class="text-blue-500 font-normal font-['Poppins'] leading-6 md:leading-[38px]">
            <VueCountdown
              v-if="counting"
              :time="15000"
              @end="counting = false;"
              v-slot="{ totalSeconds, totalMinutes }">
              {{ totalMinutes.toString().padStart(2, 0) }}:{{ totalSeconds.toString().padStart(2, 0) }}
            </VueCountdown>
          </span>
        </div>
      </div>
    </div>
    <div v-if="inLoading" class="absolute top-0 left-0 w-full h-full backdrop-blur-sm bg-white/30 text-center">
      <div class="flex justify-center items-center w-full h-full">
        <Spinner></Spinner>
      </div>
    </div>
  </Signup>
</template>

<style scoped lang="scss">

</style>
